import { IPackageConfig } from "../../types/global";
import { ComponentNames } from "../../constant/component-constant";
import { sortGenrepack } from "../../helper/genre-helper";
import { logAdapterDebug, logError } from "../../helper/logger";
import { pickChannels, pickChrome } from "../../helper/channels-service-helpers";

const ADAPTER_NAME = "ChannelPackLineupAdapter";

interface IHandleGetGenreSubscriptionsArgs {
  svodGenreData: any[];
  svodMapping: Record<string, string[]>;
  packageKey: string;
}

const handleGetGenreSubscriptions = ({
  svodGenreData,
  svodMapping,
  packageKey
}: IHandleGetGenreSubscriptionsArgs) => {
  const subscriptionData = svodGenreData?.filter?.(svodPackage => svodMapping?.[packageKey]?.includes?.(svodPackage?.productSKU));
  return subscriptionData || [];
};

// Generated by Copilot- Start
// Build an on-demand mapping by matching onDemand package ids to base svodMapping keys they include
// Example: id "ATT_TV_MYSPORTS_ORIGINAL" includes base key "ATT_TV_MYSPORTS" -> map to svodMapping["ATT_TV_MYSPORTS"]
const buildOnDemandMapping = (
  svodMapping: Record<string, string[]>,
  onDemandPackageConfig?: Array<any>
): Record<string, string[]> => {
  const mapping: Record<string, string[]> = {};
  if (!svodMapping || !onDemandPackageConfig?.length) {
    return mapping;
  }

  const baseKeys = Object?.keys(svodMapping);
  onDemandPackageConfig?.forEach(pkg => {
    const id = pkg?.id;
    if (!id) {
      return;
    }
    const matchKey = baseKeys?.find(key => id?.includes(key));
    if (matchKey) {
      mapping[id] = svodMapping[matchKey] || [];
    }
  });
  return mapping;
};
// Generated by Copilot- End

interface IHandleGetGenreChannelsArgs {
  channels: any[];
  genre: string;
}

const handleGetGenreChannels = ({
  channels,
  genre
}: IHandleGetGenreChannelsArgs) => {
  try {
    logAdapterDebug(ADAPTER_NAME, `handle get channels for genre: ${genre}`, {
      step: "handleGetGenreChannels",
      channelsCount: channels?.length || 0
    });

    if (channels) {
      const genreChannels = channels
        ?.filter?.((channel: any) => channel?.packageListSystem?.includes(genre))
        .map((channel: any) => ({
          channelNameLabel: channel?.channelNameLabel,
          channelLogoPath: channel?.channelLogoPath
        }))
        ?.sort((a, b) => a?.channelNameLabel?.localeCompare(b?.channelNameLabel));

      return genreChannels;
    }
  } catch (error) {
    logError(ADAPTER_NAME, error, {
      step: "handleGetGenreChannels"
    });
    return [];
  }
};

interface IGenreDataArgs {
  content?: any;
  packageConfig?: any[];
  onDemandPackageConfig?: any[];
  miniPacks?: string[];
  svodMapping?: Record<string, string[]>;
  onDemandMapping?: Record<string, string[]>;
  svodGenreData?: any[];
  channels?: any[];
  queryParameters?: string[];
}

const genreData = ({
  content,
  packageConfig,
  onDemandPackageConfig,
  miniPacks,
  svodMapping,
  onDemandMapping,
  svodGenreData,
  channels,
  queryParameters
}: IGenreDataArgs) => {
  try {
    logAdapterDebug(ADAPTER_NAME, "starting genre data adaptation", {
      step: "genreData"
    });
    const updatedContent = JSON.parse(JSON.stringify(content));
    updatedContent?.packages?.forEach((genrePack: any, index) => {
      const match = packageConfig?.find(
        item => item.id === genrePack?.packageKey
      );
      const onDemandMatch = onDemandPackageConfig?.find?.(
        (item: any) => item?.id === genrePack?.packageKey
      );
      const isMiniPack = miniPacks.some(mini => genrePack?.packageKey?.includes(mini.split(",")[1]));
      // Generated by Copilot- Start
      // Use onDemandMatch labels/count when its queryparam is in queryParameters (e.g., mysportsoriginal)
      const shouldUseOnDemandLabel =
          !!onDemandMatch &&
          queryParameters?.includes?.(onDemandMatch?.queryparam?.toLowerCase?.() || "");
        // Choose mapping based on whether on-demand variant is active, with fallback
      let mappingToUse: Record<string, string[]> | undefined = svodMapping;
      if (shouldUseOnDemandLabel) {
        // Fallback: if onDemandMapping[genrePack.packageKey] is missing, use svodMapping
        mappingToUse = {
          ...svodMapping,
          ...onDemandMapping
        };
      }
      // Generated by Copilot- End

      const labelSource = shouldUseOnDemandLabel ? onDemandMatch : match;
      const channelSource = shouldUseOnDemandLabel ? onDemandMatch?.channel : match?.channel;

      genrePack.packageName =
        genrePack?.packageKey === "MYFREE_DTV" ?
          genrePack?.packageName || "" :
          labelSource?.richTextDisplayLabel || labelSource?.displayLabel || "";

      genrePack.channelCount =
        (isMiniPack && !updatedContent?.showMiniGPChannelCount) ||
        (!isMiniPack && !updatedContent?.showGPChannelCount) ?
          "" :
          `${(channelSource?.displayCount || "") +
              (channelSource?.signLetter || "")
            } ${channelSource?.label || ""}`;

      const chosenQueryParam = shouldUseOnDemandLabel ?
        onDemandMatch?.queryparam :
        match?.queryparam;
      genrePack.contentFriendlyPackageKey = (chosenQueryParam || "")?.toLowerCase();
      // if -> to handle the case when query parameters are passed
      if (
        queryParameters?.length > 0 &&
        queryParameters?.includes(genrePack?.contentFriendlyPackageKey)
      ) {
        updatedContent.packages[`${index}`].channels = handleGetGenreChannels({
          channels,
          genre: genrePack?.packageKey
        });
        if (genrePack?.packageKey === "ATT_TV_MYHOMETEAM") {
          updatedContent.myHomeTeamNationalChannels =
            updatedContent.packages[`${index}`].channels || [];
        }
        updatedContent.packages[`${index}`].subscriptions = handleGetGenreSubscriptions({
          svodGenreData,
          svodMapping: mappingToUse || {},
          packageKey: genrePack?.packageKey
        })
      } else if (!queryParameters) {
        // else if -> to handle the case when no query parameters are passed (all GP's)
        updatedContent.packages[`${index}`].channels =
          handleGetGenreChannels({
            channels,
            genre: genrePack?.packageKey
          })
        if (genrePack?.packageKey === "ATT_TV_MYHOMETEAM") {
          updatedContent.myHomeTeamNationalChannels =
            updatedContent.packages[`${index}`].channels || [];
        }
        updatedContent.packages[`${index}`].subscriptions =
          handleGetGenreSubscriptions({
            svodGenreData,
            svodMapping,
            packageKey: genrePack?.packageKey
          })
      } else {
        // else -> to clean up previously added channels
        updatedContent.packages[`${index}`].channels = [];
        updatedContent.packages[`${index}`].subscriptions = [];
      }
    });
    return updatedContent;
  } catch (error) {
    logError(ADAPTER_NAME, error, {
      step: "genreData"
    });
    return content;
  }
};

interface IChannelPackLineupAdapter {
  content: any;
  channelListingData: any;
  channelsServiceData?: any;
  genreOffers: any;
  svodGenreData: any;
  genreLocalChannelMapping: any;
  packageConfig: any;
  onDemandPackageConfig: any;
  myFreeDTVConfig: any;
  genreNationals: any;
  cmsPageName?: string;
  localChannelsInPackage: any;
  globalLineupConfig?: any;
}

export const channelPackLineupAdapeter = ({
  content: contentArg,
  channelListingData,
  channelsServiceData,
  genreOffers,
  svodGenreData,
  genreLocalChannelMapping,
  localChannelsInPackage,
  packageConfig,
  onDemandPackageConfig,
  myFreeDTVConfig: _myFreeDTVConfig,
  genreNationals,
  cmsPageName,
  globalLineupConfig
}: IChannelPackLineupAdapter): any => {
  let content = contentArg;
  try {
    logAdapterDebug(
      ADAPTER_NAME,
      "starting channel pack lineup page adaptation",
      {
        cmsPageName
      }
    );

    const genrePrices = genreOffers?.offerPrices;
    const svodMapping = genreOffers?.svodMapping;
    const onDemandMapping = buildOnDemandMapping(svodMapping, onDemandPackageConfig);
    const channels =
      pickChannels(
        channelsServiceData?.channels,
        channelListingData?.[":items"]?.chnlListingPageData?.elements?.channels
      );
    const miniPacks = pickChrome(
      globalLineupConfig?.miniPacks,
      channelListingData?.[":items"]?.chnlListingPageData?.elements?.miniPacks
    )?.options;

    content.packages = sortGenrepack(
      content?.packages,
      packageConfig,
      "channelpacklineup"
    );
    // remove MyFree package and add it to the end of the array
    content?.packages?.push?.(
      ...(content?.packages?.splice?.(
        content?.packages?.findIndex?.(
          pkg => pkg?.packageKey === "MYFREE_DTV"
        ),
        1
      ) || [])
    ); // Move MYFREE_DTV to the end of the array

    content = genreData({
      content,
      packageConfig,
      miniPacks,
      svodMapping,
      onDemandMapping,
      svodGenreData,
      channels
    });

    return {
      props: {
        ...content,
        genrePrices,
        svodGenreData,
        genreLocalChannelMapping,
        genreNationals,
        localChannelsInPackage
      },
      type: ComponentNames.CHANNELPACKLINEUP
    };
  } catch (error) {
    logError(ADAPTER_NAME, error, {
      message: "Error in channelPackLineupAdapeter",
      cmsPageName
    });
  }
};

interface IDynamicChannelPackLineupAdapter {
  queryParams: any;
  componentProps?: any;
  genreOffers?: any;
  svodGenreData?: any;
  packageConfig: Array<IPackageConfig>;
  onDemandPackageConfig: Array<IPackageConfig>;
  myFreeDTVConfig: any;
  channelListingData?: any;
  channelsServiceData?: any;
  globalLineupConfig?: any;
}

export function dynamicChannelPackLineupAdapter({
  componentProps,
  queryParams,
  genreOffers,
  svodGenreData,
  packageConfig,
  onDemandPackageConfig,
  myFreeDTVConfig,
  channelListingData,
  channelsServiceData,
  globalLineupConfig
}: IDynamicChannelPackLineupAdapter) {
  try {
    logAdapterDebug(
      ADAPTER_NAME,
      "starting dynamic channel pack lineup adaptation",
      {
        queryParams
      }
    );
    const queryParameters =
      queryParams?.pkg
        ?.split(",")
        ?.map(queryParam => queryParam?.toLowerCase()) || [];

    const zipcode = typeof queryParams?.zipcode === 'string' && /^\d{5}$/.test(queryParams.zipcode.trim()) ?
      queryParams.zipcode.trim() :
      undefined;

    const svodMapping = genreOffers?.svodMapping;
    const onDemandMapping = buildOnDemandMapping(svodMapping, onDemandPackageConfig);
    const channels =
      pickChannels(
        channelsServiceData?.channels,
        channelListingData?.[":items"]?.chnlListingPageData?.elements?.channels
      );
    const miniPacks = pickChrome(
      globalLineupConfig?.miniPacks,
      channelListingData?.[":items"]?.chnlListingPageData?.elements?.miniPacks
    )?.options;
    let updatedComponentProps = {};
    if (queryParameters?.length > 0) {
      queryParameters?.push("myfreedtv");
      updatedComponentProps = genreData({
        content: componentProps,
        packageConfig,
        onDemandPackageConfig,
        miniPacks,
        svodMapping,
        onDemandMapping,
        svodGenreData,
        channels,
        queryParameters
      });
    }
    return {
      ...componentProps,
      ...updatedComponentProps,
      queryParameters: queryParameters?.length > 0 ? queryParameters : undefined,
      ...(zipcode ? { zipcode } : {})
    };
  } catch (error) {
    logError(ADAPTER_NAME, error, {
      message: "Error in dynamicChannelPackLineupAdapter"
    });
    return componentProps;
  }
}
